import React, { useState } from 'react';
import * as _ from 'lodash';
import MerchantDetailsComponentProps from '../merchantDetailsComponentProps';
import fcmAccountService from "./fcmAccountService";
import FcmAccountEditor from "./FcmAccountEditor";
import Loading from 'common/ui/Loading';
import loggingService from 'common/logging/loggingService';
import ValidationError from 'common/validationError';
import notificationService from 'common/notificationService';
import DeleteConfirmationModal, { useDeleteConfirmationModal } from 'common/ui/DeleteConfirmationModal';
import WorkingOrdersAlertModal from 'admin/features/merchants/general/WorkingOrdersAlertModal';
import Merchant, { NotificationSettings } from 'domain/merchant';
import OrderThin from 'domain/orderThin';
import merchantService, { UpdateMerchantRequest } from '../merchantService';
import { Form, OverlayTrigger, Popover } from 'react-bootstrap';
import moment from 'moment';
import './GeneralSettings.scss'
import ContractTypeSettings from './ContractTypeSettings';
import ContractTypeSetting from './contractTypeSetting';
import fcmService from './fcmService';
import Fcm from 'domain/fcm';
import FcmAccount from 'features/fcmAccounts/fcmAccount';
import NumberEditor from 'common/ui/NumberEditor';
import orderService from './orderService';
import { ContractType } from 'domain/contract';
import useAjaxCall from 'common/ui/useAjaxCall';
import { withCancellationToken } from 'admin/common/adminHttpService';
import { DeliveryMethodDetails } from 'domain/deliveryMethod';
import MerchantNotificationSettings from './MerchantNotificationSettings';
import PaymentOptionsSettings from './PaymentOptionsSettings';
import PaymentOptionDetails from 'domain/paymentOptionDetails';
import { PaymentOption } from 'domain/paymentOption';

const eodOptions = ["13:00:00", "13:30:00", "14:00:00", "15:00:00", "15:30:00", "16:00:00", "16:30:00", "17:00:00", "17:30:00", "18:00:00", "20:00:00", "21:00:00", "22:00:00", "23:00:00", "00:00:00"];

const GeneralSettings: React.FC<MerchantDetailsComponentProps> = props => {
    const [fcmAccounts, setFcmAccounts] = useState<FcmAccount[]>(null);
    const [showWorkingOrdersAlertModal, setShowWorkingOrdersAlertModal] = useState(false);
    const [workingOrdersForSelectedFcmAccount, setWorkingOrdersForSelectedFcmAccount] = useState<OrderThin[]>(null);
    const [fcms, setFcms] = useState<Fcm[]>(null);
    const [accountToDelete, setAccountToDelete] = useState<string>(null);
    const [contractTypeSettings, setContractTypeSettings] = useState<ContractTypeSetting[]>(null);
    const [deliveryMethods, setDeliveryMethods] = useState<DeliveryMethodDetails[]>(null);
    const [paymentOptionDetailsList, setPaymentOptionDetailsList] = useState<PaymentOptionDetails[]>(null);
    const [isValidated, setIsValidated] = useState(false);

    const deleteConfirmation = useDeleteConfirmationModal();

    useAjaxCall(cxt => {
        withCancellationToken(fcmAccountService, cxt).listAccounts(props.merchant.id)
            .then(m => setFcmAccounts(m));
        withCancellationToken(fcmService, cxt).listFcms()
            .then(m => setFcms(m))
        withCancellationToken(merchantService, cxt).listContractTypeSettings(props.merchant.id)
            .then(m => setContractTypeSettings(m));
        withCancellationToken(merchantService, cxt).listDeliveryMethods(props.merchant.id)
            .then(m => setDeliveryMethods(m));
        withCancellationToken(merchantService, cxt).listPaymentOptions(props.merchant.id)
            .then(m => setPaymentOptionDetailsList(m));
    }, [])

    async function handleAddFcmAccount(account: string, fcmId: number) {
        try {
            const fcm = fcms.find(f => f.id === fcmId);
            const fcmAccount: FcmAccount = {
                id: -1,
                accountNumber: account,
                fcm: fcm
            };
            await fcmAccountService.addAccount(props.merchant.id, account, fcmId);
            setFcmAccounts([...fcmAccounts, fcmAccount])
            setIsValidated(true);
        }
        catch (err) {
            loggingService.error("Unable to add FCM account", err as Error);
            const validationError = err as ValidationError;
            notificationService.error(validationError.errors?.join(", ") ?? "Unable to add FCM account");
        }
    }

    async function handleUpdateFcmAccount(account: string, fcmId: number) {
        try {
            const fcm = fcms.find(f => f.id === fcmId);
            const updatedFcmAccounts = fcmAccounts.map(m => m.accountNumber === account
                ? {...m, fcm: fcm }
                : m);
            await fcmAccountService.updateAccount(props.merchant.id, account, fcmId);
            setFcmAccounts(updatedFcmAccounts)
        }
        catch (err) {
            loggingService.error("Unable to update FCM account", err as Error);
            const validationError = err as ValidationError;
            notificationService.error(validationError.errors?.join(", ") ?? "Unable to update FCM account");
        }
    }

    async function handleRemoveFcmAccount(account: string) {
        setAccountToDelete(account);
        const merchantId = props.merchant.id;

        try {
            const workingOrders = await orderService.getWorkingOrders(merchantId, account);

            if (workingOrders.length > 0) {
                // Show modal with working orders and prevent deletion
                setWorkingOrdersForSelectedFcmAccount(workingOrders);
                setShowWorkingOrdersAlertModal(true);
            } else {
                // No working orders, ask for deletion confirmation
                deleteConfirmation.show();
            }
        } catch (error) {
            loggingService.error("Error fetching working orders", error as Error);
            notificationService.error("Failed to check working orders before deletion.");
        }
    }

    async function deleteFcmAccount() {
        try {
            await fcmAccountService.removeAccount(props.merchant.id, accountToDelete);
            setFcmAccounts(fcmAccounts.filter(m => m.accountNumber !== accountToDelete))
        }
        catch (err) {
            loggingService.error("Unable to remove FCM account", err as Error);
            const validationError = err as ValidationError;
            notificationService.error(validationError.errors?.join(", ") ?? "Unable to remove FCM account");
        }
    }

    async function handleUpdateMerchant(updatedMerchant: Merchant, request: UpdateMerchantRequest) {
        const existingMerchant = props.merchant;

        props.setMerchant(updatedMerchant);
        try {
            await merchantService.updateMerchant(props.merchant.id, request);
        }
        catch (err) {
            notificationService.error("Unable to update merchant");
            loggingService.error("Unable to update merchant", err as Error);
            props.setMerchant(existingMerchant);
        }
    }

    async function handleUpdateContractTypeSettings(settings: ContractTypeSetting[]) {
        const existingSettings = contractTypeSettings;

        setContractTypeSettings([...settings]);
        try {
            await merchantService.updateContractTypeSettings(props.merchant.id, settings);
        }
        catch (err) {
            notificationService.error("Unable to update contract type settings");
            loggingService.error("Unable to update contract type settings", err as Error);
            setContractTypeSettings(existingSettings);
        }
    }


    function isLoaded(): boolean {
        return fcmAccounts != null && contractTypeSettings != null && fcms != null;
    }

    async function handleUpdateMerchantForCustomerContractId(isCustomerContractIdForBuyDisplayed: boolean, isCustomerContractIdForSellDisplayed: boolean) {
        const isCustomerContractIdDisplayed = isCustomerContractIdForBuyDisplayed || isCustomerContractIdForSellDisplayed;
        const useCustomerContractIdAsErpId = !isCustomerContractIdDisplayed ? false : props.merchant.useCustomerContractIdAsErpId;
        const makeCustomerContractIdRequired = !isCustomerContractIdDisplayed ? false : props.merchant.makeCustomerContractIdRequired;

        await handleUpdateMerchant({ ...props.merchant, 
            displayCustomerContractIdForBuy: isCustomerContractIdForBuyDisplayed, 
            displayCustomerContractIdForSell: isCustomerContractIdForSellDisplayed,
            useCustomerContractIdAsErpId: useCustomerContractIdAsErpId,
            makeCustomerContractIdRequired: makeCustomerContractIdRequired
        }, 
        {   displayCustomerContractIdForBuy: isCustomerContractIdForBuyDisplayed,
            displayCustomerContractIdForSell: isCustomerContractIdForSellDisplayed,
            useCustomerContractIdAsErpId: useCustomerContractIdAsErpId,
            makeCustomerContractIdRequired: makeCustomerContractIdRequired 
        });
    }

    function handleContractTypeChanged(contractType: ContractType, isEnabled: boolean): void {
        const newContractTypes = isEnabled
            ? [...props.merchant.contractTypes, contractType]
            : props.merchant.contractTypes.filter(m => m != contractType);

        merchantService.updateMerchant(props.merchant.id, {
            contractTypes: newContractTypes
        });
        props.setMerchant({...props.merchant, 
            contractTypes: newContractTypes
        });
    }

    async function handleDeliveryMethodDetailsChanged(deliveryMethodDetails: DeliveryMethodDetails): Promise<any> {
        await merchantService.updateDeliveryMethod(props.merchant.id, deliveryMethodDetails);
        setDeliveryMethods(dmds => dmds.map(dmd => dmd.deliveryMethod === deliveryMethodDetails.deliveryMethod ? deliveryMethodDetails : dmd));
    }

    async function handleNotificationPreferencesChanged(notificationSettings: NotificationSettings) {
        await handleUpdateMerchant({ ...props.merchant, notificationSettings: notificationSettings }, { notificationSettings: notificationSettings })
    }

    async function handlePaymentOptionDetailsChanged(paymentOptionDetails: PaymentOptionDetails): Promise<void> {
        await merchantService.upsertPaymentOption(props.merchant.id, paymentOptionDetails);
        setPaymentOptionDetailsList(pods => pods.map(pod => pod.paymentOption === paymentOptionDetails.paymentOption ? paymentOptionDetails : pod));
    }

    if (!isLoaded()) return <Loading />;

    return <div className="general-settings">

        <div className="mt-4">
            <div className="mb-3 header">
                <h4>Merchant Settings</h4>
            </div>

            <div className="row settings-row">
                <div className="col-md-2">
                    <label className="pr-1" htmlFor="auto-position-switch">End of Day (CST)</label>
                    <OverlayTrigger placement="right" overlay={EodPopover}>
                        <i className="fa fa-info-circle pl-2"></i>
                    </OverlayTrigger>
                </div>
                <div className="col-md-1">
                    <Form.Control as="select"

                        value={props.merchant.endOfDay}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const eod = event.target.value;
                            handleUpdateMerchant({ ...props.merchant, endOfDay: eod }, { endOfDay: eod });
                        }}>
                        {eodOptions.map((time, i) => (
                            <option key={i} value={time}>
                                {moment(time, "HH:mm:ss").format("hh:mm A")}
                            </option>
                        ))}
                    </Form.Control>
                </div>
            </div>

            <div className="row settings-row">
                <div className="col-md-2">
                    <label className="pr-1" htmlFor="comment-deletion-allowed-switch">Comment Deletion Allowed</label>
                </div>
                <div className="col-md-2">
                    <Form.Check
                        type="switch"
                        id="comment-deletion-allowed-switch"
                        label=""
                        checked={props.merchant.isCommentDeletionAllowed}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const isAllowed = event.target.checked;
                            handleUpdateMerchant({ ...props.merchant, isCommentDeletionAllowed: isAllowed }, { isCommentDeletionAllowed: isAllowed })
                        }}
                    />
                </div>
            </div>

            <div className="row settings-row">
                <div className="col-md-2">
                    <label className="pr-1" htmlFor="payment-option-switch">Display Payment Option on Add Offer/Contract</label>
                </div>
                <div className="col-md-2">
                    <Form.Check
                        type="switch"
                        id="payment-option-switch"
                        label=""
                        checked={!props.merchant.isPaymentOptionHidden}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const isHidden = !event.target.checked;
                            handleUpdateMerchant({ ...props.merchant, isPaymentOptionHidden: isHidden }, { isPaymentOptionHidden: isHidden })
                        }}
                    />
                </div>
            </div>

            <div className="row settings-row">
                <div className="col-md-2">
                    <label className="pr-1" htmlFor="display-customer-contract-id-for-sell-switch">Display Cust. Contract ID on Add Offer/Contract</label>
                </div>
                <div className="col-md-1 pr-0">
                    <Form.Check
                        type="switch"
                        id="display-customer-contract-id-for-buy-switch"
                        label="Customer Will Buy"
                        checked={props.merchant.displayCustomerContractIdForBuy}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const isCustomerContractIdForBuyDisplayed = event.target.checked;
                            handleUpdateMerchantForCustomerContractId(isCustomerContractIdForBuyDisplayed, props.merchant.displayCustomerContractIdForSell);
                        }}
                    />
                </div>
                <div className="col-md-1 pl-0">
                    <Form.Check
                        type="switch"
                        id="display-customer-contract-id-for-sell-switch"
                        label="Customer Will Sell"
                        checked={props.merchant.displayCustomerContractIdForSell}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const isCustomerContractIdForSellDisplayed = event.target.checked;
                            handleUpdateMerchantForCustomerContractId(props.merchant.displayCustomerContractIdForBuy, isCustomerContractIdForSellDisplayed);
                        }}
                    />
                </div>
            </div>

            {(props.merchant.displayCustomerContractIdForSell || props.merchant.displayCustomerContractIdForBuy) && <div className="row settings-row">
                <div className="col-md-2">
                    <label className="pr-1" htmlFor="use-customer-contract-id-switch">Cust. Contract ID on Add Offer/Contract</label>
                </div>
                <div className="col-md-1 pr-0">
                    <Form.Check
                        type="switch"
                        id="use-customer-contract-id-switch"
                        label="Use as ERP ID"
                        checked={props.merchant.useCustomerContractIdAsErpId}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const isUsed = event.target.checked;
                            handleUpdateMerchant({ ...props.merchant, useCustomerContractIdAsErpId: isUsed }, { useCustomerContractIdAsErpId: isUsed })
                        }}
                    />
                </div>
                <div className="col-md-1 pl-0">
                    <Form.Check
                        type="switch"
                        id="make-customer-contract-id-required-switch"
                        label="Make Required Field"
                        checked={props.merchant.makeCustomerContractIdRequired}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const isRequired = event.target.checked;
                            handleUpdateMerchant({ ...props.merchant, makeCustomerContractIdRequired: isRequired }, { makeCustomerContractIdRequired: isRequired })
                        }}
                    />
                </div>
            </div>}

            <div className="row settings-row">
                <div className="col-md-2">
                    <label className="pr-1" htmlFor="determine-later-switch">Determine Later Allowed</label>
                </div>
                <div className="col-md-2">
                    <Form.Check
                        type="switch"
                        id="determine-later-switch"
                        label=""
                        checked={props.merchant.isDetermineLaterAllowed}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const isAllowed = event.target.checked;
                            handleUpdateMerchant({ ...props.merchant, isDetermineLaterAllowed: isAllowed }, { isDetermineLaterAllowed: isAllowed })
                        }}
                    />
                </div>
            </div>

            <div className="row settings-row">
                <div className="col-md-2">
                    <label className="pr-1" htmlFor="display-region-on-add-offer">Display Region on Add Offer/Contract</label>
                </div>
                <div className="col-md-2">
                    <Form.Check
                        type="switch"
                        id="display-region-on-add-offer-switch"
                        label=""
                        checked={props.merchant.displayRegionOnAddOffer}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const displayRegionOnAddOffer = event.target.checked;
                            handleUpdateMerchant({ ...props.merchant, displayRegionOnAddOffer: displayRegionOnAddOffer }, { displayRegionOnAddOffer: displayRegionOnAddOffer })
                        }}
                    />
                </div>
            </div>

            <div className="row settings-row">
                <div className="col-md-2">
                    <label className="pr-1" htmlFor="display-contract-location">Display Contract Location</label>
                </div>
                <div className="col-md-2">
                    <Form.Check
                        type="switch"
                        id="display-contract-location-switch"
                        label=""
                        checked={props.merchant.displayContractLocation}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const displayContractLocation = event.target.checked;
                            handleUpdateMerchant({ ...props.merchant, displayContractLocation: displayContractLocation }, { displayContractLocation: displayContractLocation })
                        }}
                    />
                </div>
            </div>

            <div className="row settings-row">
                <div className="col-md-2">
                    <label className="pr-1" htmlFor="use-market-order">Use Market Orders when Auto Offer ON</label>
                </div>
                <div className="col-md-2">
                    <Form.Check
                        type="switch"
                        id="use-market-order"
                        label=""
                        checked={props.merchant.marketAutoOfferEnabled}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const enableMarketOrder = event.target.checked;
                            handleUpdateMerchant({ ...props.merchant, marketAutoOfferEnabled: enableMarketOrder }, { marketAutoOfferEnabled: enableMarketOrder })
                        }}
                    />
                </div>
            </div>
        </div>

        <div className="mt-4">
            <div className="mb-3 header">
                <h4>Contract Type Settings</h4>
            </div>
            <div className="mt-4">
                <ContractTypeSettings
                    contractTypeSettings={contractTypeSettings}
                    deliveryMethods={deliveryMethods}
                    enabledContractTypes={props.merchant.contractTypes}
                    onChange={m => {
                        const newSettings = _.unionWith([m], contractTypeSettings, (m, x) => x.contractType === m.contractType && x.deliveryMethod === m.deliveryMethod);
                        handleUpdateContractTypeSettings(newSettings);
                    }}
                    onContractTypeChanged={handleContractTypeChanged}
                    onDeliveryMethodDetailsChanged={handleDeliveryMethodDetailsChanged} />
            </div>
        </div>

        <div className="mt-4">
            <div className="mb-3 header">
                <h4>Payment Options Settings</h4>
            </div>
            <div className="mt-4">
                <PaymentOptionsSettings
                    paymentOptionDetails={paymentOptionDetailsList}
                    onChange={(pod => handlePaymentOptionDetailsChanged(pod))} />
            </div>
        </div>

        <div className="mt-4">
            <div className="mb-3 header">
                <h4>FCM Accounts</h4>
            </div>
            <FcmAccountEditor
                accounts={fcmAccounts}
                fcms={fcms}
                onAdd={handleAddFcmAccount}
                onUpdate={handleUpdateFcmAccount}
                onRemove={handleRemoveFcmAccount}
                showValidationErrors={isValidated}
            />
            <div className="row mt-4">
                <div className="col-md-2 mt-2">
                    <label className="pr-1" htmlFor="Give-Up Fee">Give-Up Fee</label>
                </div>
                <div className="col-md-1">
                    <NumberEditor className="form-control"
                        required
                        allowDecimals
                        precision={4}
                        enforcePrecision
                        value={props.merchant.giveUpFee}
                        onValueChange={value => value != props.merchant.giveUpFee ? handleUpdateMerchant({ ...props.merchant, giveUpFee: value }, { giveUpFee: value }) : {}}
                    />
                </div>
            </div>
        </div>

        <MerchantNotificationSettings notificationSettings={props.merchant.notificationSettings} onChange={handleNotificationPreferencesChanged}/> 
        
        <DeleteConfirmationModal controller={deleteConfirmation}
            title="Delete FCM Account?"
            text={`Are you sure you want to delete the FCM account '${accountToDelete}'?`}
            onConfirm={deleteFcmAccount}
            onCancel={() => setAccountToDelete(null)}
        />

        <WorkingOrdersAlertModal
            showModal={showWorkingOrdersAlertModal}
            orders={workingOrdersForSelectedFcmAccount}
            onWorkingOrdersAlertModalCancel={() => setShowWorkingOrdersAlertModal(false)}
            accountName={accountToDelete}
        />
    </div>;
}

const EodPopover = (
    <Popover id="eodPopover">
        <Popover.Content>
            <p>To avoid unintended consequences, EOD should be changed as close as possible to the current EOD</p>
            <p>For example, if changing from 5 PM to 9 PM, 5:01 PM would be best</p>
        </Popover.Content>
    </Popover>
);

export default GeneralSettings;